// Telegram-специфика как ОПЦИОНАЛЬНОЕ улучшение (barter-024): все обращения
// к платформе ТГ идут через безопасный доступор services/twa — вне Telegram
// каждый вызов просто no-op/false, загрузка приложения не зависит от SDK.

import { twa, loadTwa } from './twa';
import { getDesign, type DesignId } from './design';

// [шапка, фон] вебвью Telegram под каждый дизайн × тему
const DESIGN_TG_COLORS: Record<DesignId, { light: [`#${string}`, `#${string}`]; dark: [`#${string}`, `#${string}`] }> = {
  neo: { light: ['#ffffff', '#f4f5f8'], dark: ['#0b0d12', '#0b0d12'] },
  classic: { light: ['#ffffff', '#f5f5f5'], dark: ['#1a1a2e', '#16213e'] },
  air: { light: ['#ffffff', '#f7f8fb'], dark: ['#1c1d24', '#131419'] },
  glass: { light: ['#6366f1', '#8b5cf6'], dark: ['#1e1b4b', '#312e81'] },
  aurora: { light: ['#0b0f1a', '#0b0f1a'], dark: ['#0b0f1a', '#0b0f1a'] },
  ukraine: { light: ['#0057B7', '#F7FAFF'], dark: ['#0A1A2F', '#0A1A2F'] },
  leaf: { light: ['#ffffff', '#F6FAF6'], dark: ['#0D1712', '#0D1712'] },
  vyshyvanka: { light: ['#FAF3E7', '#FAF3E7'], dark: ['#241C16', '#241C16'] },
};

export function applyTelegramColors(design: DesignId, theme: 'light' | 'dark') {
  const app = twa();
  if (!app) return;
  try {
    const [header, bg] = DESIGN_TG_COLORS[design][theme];
    app.setHeaderColor(header);
    app.setBackgroundColor(bg);
  } catch {}
}

// ---------------------------------------------------------------------------
// Тема: ручной выбор пользователя («Темна» по умолчанию, есть «Світла» и
// «Системна» — следовать за Telegram/ОС). Живёт в localStorage, пре-пейнт
// скрипт в index.html применяет её до загрузки бандла, чтобы не было вспышки.
// ---------------------------------------------------------------------------

export type ThemePreference = 'dark' | 'light' | 'system';

const THEME_KEY = 'barter-theme';

export function getThemePreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === 'dark' || stored === 'light' || stored === 'system') return stored;
  } catch {}
  return 'dark';
}

export function saveThemePreference(pref: ThemePreference) {
  try {
    localStorage.setItem(THEME_KEY, pref);
  } catch {}
}

/** Финальная тема с учётом выбора: системная → тема Telegram/ОС, иначе выбор. */
export function getTheme(pref: ThemePreference = getThemePreference()): 'light' | 'dark' {
  if (pref !== 'system') return pref;
  const app = twa();
  if (app && app.colorScheme === 'light') return 'light';
  try {
    return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

/**
 * Инициализация платформы: внутри Telegram подгружает SDK, разворачивает
 * вебвью и красит шапку под дизайн; в обычном браузере — no-op (web-first).
 */
export async function initPlatform(): Promise<void> {
  const app = await loadTwa();
  if (!app) return;
  try {
    app.ready();
    app.expand();
  } catch {}
  applyTelegramColors(getDesign(), getTheme());
}

export function getTelegramUser() {
  const app = twa();
  try {
    const user = app?.initDataUnsafe?.user;
    return user
      ? {
          id: user.id,
          firstName: user.first_name,
          lastName: user.last_name || '',
          username: user.username || '',
        }
      : null;
  } catch {
    return null;
  }
}

export function isTelegram() {
  const app = twa();
  try {
    return Boolean(app) && app.platform !== 'unknown';
  } catch {
    return false;
  }
}

/** Запрос телефона у Telegram (только в Mini App). Вне ТГ — false. */
export function requestTwaPhone(cb: (phone: string) => void): boolean {
  const app = twa();
  if (!app || typeof app.requestContact !== 'function') return false;
  try {
    app.requestContact((allowed: boolean, contact: { phone_number?: string }) => {
      if (allowed && contact?.phone_number) cb(contact.phone_number);
    });
    return true;
  } catch {
    return false;
  }
}

/** Открытие t.me-ссылки через WebApp. Вне ТГ — false (звонит обычная ссылка). */
export function openTwaLink(url: string): boolean {
  const app = twa();
  if (!app || typeof app.openTelegramLink !== 'function') return false;
  try {
    app.openTelegramLink(url);
    return true;
  } catch {
    return false;
  }
}
